/**
 * LiveLayer
 *
 * Wrapper implementation for existing BaseLayerProvider instances.
 * This allows existing live layers to work with the new layer system
 * without modifying their code.
 *
 * Expected config structure:
 * {
 *   "providerId": "weather-stations",              // Unique provider ID
 *   "providerClass": "WeatherLayerProvider",       // Provider class name
 *   "providerConfig": { ... },                     // Provider-specific config
 *   "refreshInterval": 300000                      // Auto-refresh interval (optional)
 * }
 *
 * Note: This is an adapter that wraps existing BaseLayerProvider implementations.
 * It does NOT modify the existing live layer code.
 */

import mapboxgl from 'mapbox-gl';
import { BaseLayer, LayerConfig } from './BaseLayer';
import { LayerProvider, CleanupReason } from '../liveLayers/interfaces';
import { visibilityAwareInterval, VisibilityAwareIntervalHandle } from './utils/visibilityAwareInterval';
import { isInRefreshTimeRange } from './utils/refreshTimeRange';

/**
 * Recursively flatten nested objects into dotted-key form. Ported from
 * LiveMapLayer.tsx so marker functions that look up `props['header.name']`
 * style keys find the values after merge — without this, the merge step
 * leaves nested objects in place and the provider can't read them.
 */
function flattenNestedProperties(obj: any, prefix: string = ''): any {
  const result: any = {};
  for (const key in obj) {
    if (!Object.prototype.hasOwnProperty.call(obj, key)) continue;
    const val = obj[key];
    const newKey = prefix ? `${prefix}.${key}` : key;
    if (val && typeof val === 'object' && !Array.isArray(val)) {
      Object.assign(result, flattenNestedProperties(val, newKey));
    } else {
      result[newKey] = val;
    }
  }
  return result;
}

export class LiveLayer extends BaseLayer {
  /**
   * The wrapped layer provider instance
   */
  private provider: LayerProvider;

  /**
   * Data cache
   */
  private dataCache: Record<string, GeoJSON.FeatureCollection> = {};

  /**
   * Merged/processed data
   */
  private processedData: GeoJSON.FeatureCollection | null = null;

  /**
   * Mapbox source and layer IDs
   */
  private sourceId: string;
  private layerIds: string[] = [];

  /**
   * Auto-refresh handle (visibility-aware).
   */
  private refreshHandle: VisibilityAwareIntervalHandle | null = null;

  /**
   * Cleanup handlers
   */
  private cleanupHandlers: Array<(reason: CleanupReason) => void> = [];

  /**
   * Why the current teardown is happening, for the cleanup handlers.
   *
   * #863 — refresh() tears a markerFunction provider down and builds it back up
   * every cycle, so a provider's cleanup cannot otherwise tell that teardown from
   * the user switching the layer off. Only refresh() sets this; everything else
   * is a real removal.
   */
  private teardownReason: CleanupReason = 'layer-off';

  /**
   * HTML markers (if provider uses marker function)
   */
  private markers: mapboxgl.Marker[] = [];

  /**
   * Constructor
   *
   * @param map Mapbox GL map instance
   * @param config Layer configuration from database
   * @param provider The BaseLayerProvider instance to wrap
   */
  constructor(map: mapboxgl.Map, config: LayerConfig, provider: LayerProvider) {
    super(map, config);
    this.provider = provider;
    this.sourceId = `live-source-${config.id}`;

    // #822 — a provider that changes its own request (a different query string,
    // say) owns the source config but not the fetching. Hand it a way to ask
    // for the re-fetch, so a change is not hidden until the next interval.
    this.provider.setRefreshRequester?.(() => {
      this.refresh().catch(err =>
        console.error('Failed to refresh LiveLayer on provider request:', err)
      );
    });
  }

  /**
   * Initialize layer
   */
  protected async onInitialize(): Promise<void> {
    // Validate provider
    if (!this.provider) {
      throw new Error('LiveLayer requires a LayerProvider instance');
    }

    // Fetch initial data
    await this.fetchData();
    this.processData();
  }

  /**
   * Enable layer on map.
   *
   * Style-load guard: if the basemap style hasn't resolved yet, markers
   * land at wrong positions and addLayer calls fail. `idle` is a
   * guaranteed-after-paint signal where isStyleLoaded() === true.
   */
  protected async onEnable(): Promise<void> {
    if (!this.map.isStyleLoaded()) {
      this.map.once('idle', () => {
        this.onEnable().catch(err => console.error('LiveLayer deferred onEnable failed:', err));
      });
      return;
    }

    // Stale-window guard: the user can leave a tab open for hours with this
    // layer off (so auto-refresh never started), then flip it on. Without a
    // fresh fetch here, the first render uses whatever onInitialize captured
    // at app load — possibly empty (initial fetch failed / 401) or hours
    // stale. Refetch + reprocess before rendering so the marker function
    // gets current data. (fetchData populates this.dataCache; processData
    // assigns this.processedData — both are required for the render below.)
    //
    // Not when refresh() is the caller: it has just fetched, and fetching again
    // here sent every refresh tick to the server twice (#866 M5).
    if (this.teardownReason !== 'refresh') {
      try {
        await this.fetchData();
        this.processData();
      } catch (err) {
        console.warn('LiveLayer: onEnable refetch failed, rendering with cached data:', err);
      }
    }

    const renderingConfig = this.provider.renderingConfig;

    // markerFunction (DOM markers like wind barbs) OR styleFunction (native
    // Mapbox layer). Never both — providers pick the rendering style they
    // need. Trying to call addMapboxLayers for markerFunction-only providers
    // throws "IDs can't be empty" because their styleFunction is undefined.
    if (renderingConfig.markerFunction && this.processedData) {
      const helpers = this.provider.createLayerHelpers();
      renderingConfig.markerFunction(this.map, this.processedData, helpers);
      if (renderingConfig.cleanupFunction) {
        this.cleanupHandlers.push((reason) => renderingConfig.cleanupFunction!(this.map, reason));
      }
      // #625 — markers are created at full opacity. If the user's opacity was
      // restored from persistence (or set before enable), apply it now so the
      // layer loads dimmed. Opacity is applied to the marker content child
      // (see markerOpacity), which survives mapbox repositioning on zoom/pan, so
      // no per-frame re-apply is needed.
      if (this.getOpacity() < 1) {
        await this.onSetOpacity(this.getOpacity());
      }
    } else {
      this.addMapboxLayers();
    }

    this.addInteractionHandlers();

    const refreshInterval = this.config.config.refreshInterval || this.provider.options.refreshInterval;
    if (refreshInterval && refreshInterval > 0) {
      this.startAutoRefresh(refreshInterval);
    }
  }

  /**
   * Disable layer on map
   */
  protected async onDisable(): Promise<void> {
    // Stop auto-refresh
    this.stopAutoRefresh();

    // Remove interaction handlers
    this.removeInteractionHandlers();

    // Hide or remove layers based on type
    if (this.markers.length > 0) {
      // Remove HTML markers
      this.markers.forEach(marker => marker.remove());
      this.markers = [];
    } else {
      // Hide Mapbox layers
      this.layerIds.forEach(layerId => {
        if (this.map.getLayer(layerId)) {
          this.map.setLayoutProperty(layerId, 'visibility', 'none');
        }
      });
    }

    // Run cleanup handlers
    this.cleanupHandlers.forEach(cleanup => cleanup(this.teardownReason));
    this.cleanupHandlers = [];
  }

  /**
   * Update layer opacity
   */
  protected async onSetOpacity(opacity: number): Promise<void> {
    // Update opacity for all Mapbox layers
    this.layerIds.forEach(layerId => {
      if (!this.map.getLayer(layerId)) return;

      const layer = this.map.getLayer(layerId);
      if (!layer) return;

      // Update opacity based on layer type
      switch (layer.type) {
        case 'circle':
          this.map.setPaintProperty(layerId, 'circle-opacity', opacity);
          break;
        case 'line':
          this.map.setPaintProperty(layerId, 'line-opacity', opacity);
          break;
        case 'fill':
          this.map.setPaintProperty(layerId, 'fill-opacity', opacity);
          break;
        case 'symbol':
          this.map.setPaintProperty(layerId, 'icon-opacity', opacity);
          this.map.setPaintProperty(layerId, 'text-opacity', opacity);
          break;
      }
    });

    // #606 — marker-based providers (Weather, Smoke, AFF) render HTML markers,
    // not mapbox layers, so the paint loop above misses them. Let the provider
    // dim its own markers via the optional setOpacity hook.
    this.provider.setOpacity?.(opacity);
  }

  /**
   * Update layer order
   */
  protected onSetOrder(order: number): void {
    // Move all layers to appropriate position
    // Implementation would depend on LayerRegistry
  }

  /**
   * Destroy layer and clean up resources
   */
  protected async onDestroy(): Promise<void> {
    // Stop auto-refresh
    this.stopAutoRefresh();

    // Remove interaction handlers
    this.removeInteractionHandlers();

    // Remove HTML markers
    this.markers.forEach(marker => marker.remove());
    this.markers = [];

    // Remove Mapbox layers
    this.layerIds.forEach(layerId => {
      if (this.map.getLayer(layerId)) {
        this.map.removeLayer(layerId);
      }
    });

    // Remove source
    if (this.map.getSource(this.sourceId)) {
      this.map.removeSource(this.sourceId);
    }

    // Run cleanup handlers
    this.cleanupHandlers.forEach(cleanup => cleanup('layer-off'));
    this.cleanupHandlers = [];

    // Clear data
    this.dataCache = {};
    this.processedData = null;
  }

  /**
   * Get Mapbox layer IDs
   */
  public getMapboxLayerIds(): string[] {
    return this.layerIds;
  }

  /**
   * Get Mapbox source IDs
   */
  public getMapboxSourceIds(): string[] {
    return [this.sourceId];
  }

  /**
   * Manually refresh layer data
   */
  public async refresh(): Promise<void> {
    // A failed fetch leaves the last data in place. Rebuilding from that same
    // data would present it as if it had just arrived, so nothing on the map
    // is touched until a fetch succeeds.
    if (!(await this.fetchData())) return;
    this.processData();

    // Update rendering if layer is enabled
    if (this.state.isEnabled) {
      // markerFunction providers (AFF, Live Weather, smokes) keep their DOM
      // markers in provider-owned caches — this.markers stays empty for them
      // and getSource() returns undefined, so the old `this.markers.length`
      // gate left them stuck on stale data. Mirror onEnable's branch: any
      // markerFunction provider takes the disable→enable path so the
      // provider's cleanupFunction tears down the old markers before
      // markerFunction recreates them with fresh data.
      const hasMarkerFunction = !!this.provider.renderingConfig.markerFunction;
      if (hasMarkerFunction || this.markers.length > 0) {
        // #863 — say that this teardown is a refresh, so a provider can keep the
        // state that must survive a refresh but not the layer being switched off.
        this.teardownReason = 'refresh';
        try {
          await this.disable();
          await this.enable();
        } finally {
          this.teardownReason = 'layer-off';
        }
        // #606 — markers were just recreated without opacity; re-apply the
        // current opacity so a dimmed live layer stays dimmed after refresh.
        if (this.state.isEnabled && this.getOpacity() < 1) {
          await this.onSetOpacity(this.getOpacity());
        }
      } else {
        // Update GeoJSON source
        const source = this.map.getSource(this.sourceId) as mapboxgl.GeoJSONSource;
        if (source && this.processedData) {
          source.setData(this.processedData);
        }
      }
    }
  }

  /**
   * Fetch data from all provider sources.
   *
   * @returns whether every source was fetched; on failure the previous data
   *   stays cached
   */
  private async fetchData(): Promise<boolean> {
    const sources = this.provider.dataConfig.sources;
    const fetchPromises: Promise<void>[] = [];
    let ok = true;

    for (const [sourceName, sourceConfig] of Object.entries(sources)) {
      const promise = fetch(sourceConfig.url, {
        credentials: 'include'
      })
        .then(response => {
          if (!response.ok) {
            throw new Error(`Failed to fetch ${sourceName}: ${response.statusText}`);
          }
          return response.json();
        })
        .then(data => {
          this.dataCache[sourceName] = data;
        })
        .catch(error => {
          ok = false;
          console.error(`Error fetching ${sourceName}:`, error);
        });

      fetchPromises.push(promise);
    }

    await Promise.all(fetchPromises);
    return ok;
  }

  /**
   * Process and merge data according to provider config
   */
  private processData(): void {
    const processingConfig = this.provider.processingConfig;

    if (!processingConfig || !processingConfig.mainSource) {
      return;
    }

    const mainSourceName = processingConfig.mainSource.sourceName;
    const mainData = this.dataCache[mainSourceName];

    if (!mainData) {
      return;
    }

    // Create a deep copy
    const processed: GeoJSON.FeatureCollection = JSON.parse(JSON.stringify(mainData));

    // Create lookup map
    const idKey = processingConfig.mainSource.idKey;
    const featureLookup: Record<string, GeoJSON.Feature> = {};

    processed.features?.forEach(feature => {
      if (feature.properties) {
        const keyValue = feature.properties[idKey];
        if (keyValue) {
          featureLookup[keyValue] = feature;
        }
      }
    });

    // Process merge directives
    const mergeDirectives = processingConfig.mainSource.mergeDirectives;
    if (mergeDirectives) {
      mergeDirectives.forEach(directive => {
        const targetData = this.dataCache[directive.targetSource];
        if (!targetData) return;

        targetData.features?.forEach(targetFeature => {
          if (!targetFeature.properties) return;

          const targetKey = directive.targetKey;
          const sourceKey = directive.sourceKey || targetKey;
          const keyValue = targetFeature.properties[targetKey];

          if (keyValue && featureLookup[keyValue]) {
            // Flatten nested objects (e.g. {header: {name, xmitDateUtcStr}})
            // into dotted keys (`header.name`, `header.xmitDateUtcStr`) so
            // marker functions that look up `props['header.xmitDateUtcStr']`
            // find the values. Without this, the WeatherLayerProvider
            // freshness filter drops every feature, then the second-pass
            // marker creation uses stale/unmerged data and the wind barbs
            // cluster at default coords until the map repaints.
            const flattenedProps = flattenNestedProperties(targetFeature.properties);
            Object.assign(
              featureLookup[keyValue].properties!,
              flattenedProps
            );
          }
        });
      });
    }

    this.processedData = processed;
  }

  /**
   * Add Mapbox GL layers using provider's style function
   */
  private addMapboxLayers(): void {
    if (!this.processedData) return;

    const renderingConfig = this.provider.renderingConfig;
    const helpers = this.provider.createLayerHelpers();

    // Add source
    if (!this.map.getSource(this.sourceId)) {
      this.map.addSource(this.sourceId, {
        type: 'geojson',
        data: this.processedData
      });
    }

    // Get layer definition from style function. styleFunction may return a
    // partial spec without `id`; fall back to a deterministic id derived
    // from the layer's config id so mapbox-gl doesn't throw "IDs can't be
    // empty" on addLayer.
    if (renderingConfig.styleFunction) {
      const layerDef = renderingConfig.styleFunction(this.processedData, helpers);
      layerDef.source = this.sourceId;
      if (!layerDef.id) layerDef.id = `live-layer-${this.config.id}`;

      if (!this.map.getLayer(layerDef.id)) {
        this.map.addLayer(layerDef);
        this.layerIds.push(layerDef.id);
      } else {
        this.map.setLayoutProperty(layerDef.id, 'visibility', 'visible');
      }
    }
  }

  /**
   * Add click and hover interaction handlers
   */
  private addInteractionHandlers(): void {
    // Implementation would add click/hover handlers based on provider config
    // This is a placeholder for the actual implementation
  }

  /**
   * Remove interaction handlers
   */
  private removeInteractionHandlers(): void {
    // Implementation would remove event listeners
    // This is a placeholder for the actual implementation
  }

  /**
   * Start auto-refresh timer. Visibility-aware (pauses in background tabs).
   * Gated by refreshTimeRange when configured.
   */
  private startAutoRefresh(interval: number): void {
    this.stopAutoRefresh();

    this.refreshHandle = visibilityAwareInterval(() => {
      const range = (this.config.config as any).refreshTimeRange;
      if (range && !isInRefreshTimeRange(range)) return;
      this.refresh().catch(err => console.error('Failed to refresh LiveLayer:', err));
    }, interval);
  }

  /**
   * Stop auto-refresh timer
   */
  private stopAutoRefresh(): void {
    if (this.refreshHandle) {
      this.refreshHandle.cancel();
      this.refreshHandle = null;
    }
  }
}
